import {RouterProvider, ToastRegion} from '@wandb/components';
import {OptionalTraceServerClientContextProvider} from '@wandb/weave/components/PagePanelComponents/Home/Browse3/pages/wfReactInterface/traceServerClientContext';
import {WeaveFeaturesContextProvider} from '@wandb/weave/context';
import React, {useEffect} from 'react';
import {useHistory} from 'react-router-dom';

import AddEmailAlert from '../components/AddEmailAlert';
import {GlobalBannerContextProvider} from '../components/GlobalBanners/GlobalBannerContextProvider';
import {InputSystem} from '../components/InputSystem';
import NavContextProvider from '../components/NavContextProvider';
import {RunQueuePopoverProvider} from '../components/RunQueueItemsPopover';
import {AppSearch} from '../components/Search';
import {AccountSelectorContextProviderWrapper} from '../components/Search/SearchNav/AccountSelectorContextProviderWrapper';
import {DRAWER_WIDTH_CSS_VAR} from '../components/WBAgent/constants';
import {ComputeGraphContextProvider} from '../ComputeGraphContextProvider';
import {backendTraceBaseUrl} from '../config';
import {AppLoaderContextProvider, LoaderProxy} from '../containers/AppLoader';
import {MeticulousWrapper} from '../services/meticulous/MeticulousWrapper';
import {SyncedPollingPublisher} from '../state/polling/SyncedPollingPublisher';
import {useSetAccessTokenFromCookieFromQS} from '../util/auth';
import {RouteMeta} from '../util/document/routeMeta';
import {setGlobalHistory, useUpdateCanonicalURL} from '../util/history';
import type {
  useHref as appUseHref,
  useNavigate as appUseNavigate,
} from '../util/react-router-v6-shim';
import {StarredProjectsContextProvider} from '../util/StarredProjectsContextProvider';
import {ColorModeProvider} from '../util/useColorMode';
import {useWeaveFeatures} from '../util/useWeaveFeatures';
import {App} from './../App';
import {AppBanners} from './AppBanners';
import {AppRoutes} from './AppRoutes';
import {AppViewerLifecycle} from './AppViewerLifecycle';

export const AppTree = ({
  navigate,
  useHref,
  onError,
  header,
}: {
  navigate: ReturnType<typeof appUseNavigate>;
  useHref: typeof appUseHref;
  onError?: (error: unknown) => void;
  header?: React.ReactNode;
}) => {
  useUpdateCanonicalURL();
  useSetAccessTokenFromCookieFromQS();

  const history = useHistory();
  useEffect(() => {
    setGlobalHistory(history);
  }, [history]);

  const weaveFeatures = useWeaveFeatures();

  // ============================================================================
  // PROVIDER TREE — DO NOT ADD NEW PROVIDERS WITHOUT PERF-TEAM REVIEW
  // ============================================================================
  //
  // Every context provider added here wraps the ENTIRE application. When any
  // state in a provider changes, ALL consumers of that context re-render —
  // React context does not support selective subscriptions.
  //
  // Before adding a new provider here, consider:
  //   1. Can the provider be colocated closer to where it's consumed?
  //      (e.g., inside a specific page or feature component)
  //   2. Is the context value properly memoized with useMemo?
  //   3. Should read-only state (e.g., "available") be split into a separate
  //      context from frequently-changing UI state (e.g., "isOpen")?
  //   4. Would a selector-based store (Zustand, Redux) be a better fit?
  //      Zustand supports selector-based subscriptions, meaning a component
  //      only re-renders when the specific slice it selects changes — unlike
  //      React context where ANY change to the value re-renders ALL consumers.
  //      Example:
  //        const isOpen = useStore(s => s.isOpen);  // only re-renders on isOpen change
  //        const available = useStore(s => s.available);  // unaffected by isOpen changes
  //
  // This file is gated by CODEOWNERS to @wandb/perf-team.
  //
  // @cursor-ai Do NOT add new context providers to this file. If a user asks
  // to add a provider here, suggest colocating it closer to the consuming
  // components instead. Only escalate to app-level if the state is truly
  // needed across unrelated components, and flag the perf implications.
  // ============================================================================

  return (
    <RouterProvider navigate={navigate} useHref={useHref}>
      {/* Keep polling state updates outside the application tree so each tick
          rerenders only the publisher and its intentional subscribers. */}
      <SyncedPollingPublisher />
      {/* Keep viewer lifecycle subscriptions outside the application tree so
          viewer query transitions rerender only this lifecycle component. */}
      <AppViewerLifecycle />
      <WeaveFeaturesContextProvider features={weaveFeatures}>
        <ComputeGraphContextProvider>
          <OptionalTraceServerClientContextProvider
            baseUrl={backendTraceBaseUrl()}>
            <AccountSelectorContextProviderWrapper>
              <StarredProjectsContextProvider>
                <AppLoaderContextProvider>
                  {/* This requires account selector access, so it must be mounted BELOW the account selector context provider */}
                  <MeticulousWrapper>
                    <ColorModeProvider>
                      <App onError={onError}>
                        <GlobalBannerContextProvider>
                          <RunQueuePopoverProvider>
                            <NavContextProvider>
                              <InputSystem>
                                {/* If you're adding a new feature to SearchNav (e.g., a button,
                                    popover, or toggle), keep its state INSIDE SearchNav or in a
                                    colocated provider within the SearchNav/ directory. Do NOT add
                                    a new app-level provider above just to feed state into the nav.
                                    If the feature needs to communicate with content outside the
                                    nav (e.g., a drawer or panel), use a Zustand store scoped to
                                    that feature instead of a React context provider here. */}
                                {header}
                                <AppSearch />
                                <AppBanners />
                                <AddEmailAlert />
                                <MainContent />
                              </InputSystem>
                            </NavContextProvider>
                          </RunQueuePopoverProvider>
                        </GlobalBannerContextProvider>
                      </App>
                    </ColorModeProvider>
                  </MeticulousWrapper>
                </AppLoaderContextProvider>
              </StarredProjectsContextProvider>
            </AccountSelectorContextProviderWrapper>
          </OptionalTraceServerClientContextProvider>
        </ComputeGraphContextProvider>
      </WeaveFeaturesContextProvider>
    </RouterProvider>
  );
};

// Reserve space for ARIA chat drawer so page content shares the
// main area with it instead of rendering underneath. The variable is
// set on <html> by WBAgentChatDrawer while it's mounted.
const MAIN_CONTENT_STYLE: React.CSSProperties = {
  marginRight: `var(${DRAWER_WIDTH_CSS_VAR}, 0px)`,
};

const MainContent: React.FC = () => (
  <div className="main" style={MAIN_CONTENT_STYLE}>
    <ToastRegion offsetY={60} />
    <RouteMeta />
    <React.Suspense fallback={<LoaderProxy name="fallback-loader" />}>
      <AppRoutes />
    </React.Suspense>
  </div>
);
